// v1.29.20 — Shared helpers for the Payment Mode dropdown / display so
// every entry point (Rectify Payment, New Entry, Cash / Value Received,
// Day Book filter, Cash Expenses, Payment Modes Report, …) sorts A-Z
// case-insensitively and normalises the display casing to Title Case at
// RENDER time. Bug source list this fixes:
//   • Random-order dropdowns (unsorted result from /payment-modes).
//   • Mixed ALL CAPS (e.g. "CASH RECIVED") vs Title Case ("Bill Payment").
//   • Historical typo: "CASH RECIVED" → "Cash Received".
//
// Any new mode created via POST /payment-modes automatically drops into
// the right alphabetical slot on the next render — no per-callsite sort
// call needed.

const _KNOWN_ACRONYMS = new Set([
    "UPI", "NEFT", "RTGS", "IMPS", "USDT", "USD", "INR", "GPay", "ATM",
    "POS", "QR", "NPCI", "TDS", "GST", "PoS",
]);

/**
 * Normalise a single payment-mode name into presentational Title Case.
 * Idempotent — safe to call on an already-clean name.
 *
 *   "CASH RECIVED"     → "Cash Received"
 *   "cash received"    → "Cash Received"
 *   "bill payment"     → "Bill Payment"
 *   "upi transfer"     → "UPI Transfer"          (acronym preserved)
 *   "Neft (HDFC)"      → "NEFT (Hdfc)"           (acronym preserved)
 */
export function formatPaymentModeName(raw) {
    if (raw == null) return "";
    const s = String(raw).trim();
    if (!s) return "";
    // Fix known typos before title-casing so the corrected spelling is
    // what gets capitalised, not the wrong one.
    const typoFixed = s.replace(/recived/gi, "Received");
    return typoFixed
        .split(/(\s+)/) // keep whitespace runs intact
        .map((tok) => {
            if (!tok.trim()) return tok;
            const upper = tok.toUpperCase();
            if (_KNOWN_ACRONYMS.has(upper)) return upper;
            // Title case: first letter up, rest lower — keep punctuation.
            return tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase();
        })
        .join("");
}

/**
 * Return a NEW sorted array of payment-mode objects, A-Z case-insensitive.
 * Each object's `name` is also formatted to Title Case so the caller can
 * render `{m.name}` directly without a second helper call.
 */
export function sortPaymentModes(list) {
    if (!Array.isArray(list)) return [];
    return [...list]
        .map((m) => ({ ...m, name: formatPaymentModeName(m?.name) }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" }));
}

/**
 * Convenience for the Day Book filter etc. that hand around plain strings.
 * Sorts + normalises casing in one pass.
 */
export function sortPaymentModeStrings(list) {
    if (!Array.isArray(list)) return [];
    return [...list]
        .map((n) => formatPaymentModeName(n))
        .sort((a, b) => (a || "").localeCompare(b || "", "en", { sensitivity: "base" }));
}
